import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Label } from "@/components/ui/label";
import { StickyNote, ExternalLink, Lock } from "lucide-react";
import { boardTypeFromContext, getBoardRoutePath } from "@/lib/board-types";

interface NoteData {
  id: string;
  content: string;
  updatedAt: string | null;
  updatedBy: { id: string; username: string } | null;
  lockedBy: { id: string; username: string | null } | null;
  lockedAt: string | null;
}

interface TaskNotesSummaryProps {
  taskId: string;
  fromContext?: string;
}

function formatTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function hasContent(html: string): boolean {
  const stripped = html.replace(/<[^>]*>/g, "").trim();
  return stripped.length > 0;
}

export function TaskNotesSummary({ taskId, fromContext }: TaskNotesSummaryProps) {
  const { data } = useQuery<{ note: NoteData | null }>({
    queryKey: ["/api/notes", "task", taskId],
    enabled: !!taskId,
  });

  const note = data?.note ?? null;
  const noteExists = note && hasContent(note.content);
  const isLocked = !!note?.lockedBy;

  return (
    <div className="space-y-1.5" data-testid="task-notes-summary">
      <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1" data-testid="label-notes-summary">
        <StickyNote className="w-3 h-3" /> Notes
      </Label>

      <Link
        href={`${getBoardRoutePath(boardTypeFromContext(fromContext))}/${taskId}#notes-section`}
        className="flex items-center gap-2 text-sm hover-elevate rounded-md px-2 py-1.5 -mx-2"
        data-testid="link-view-notes"
      >
        <ExternalLink className="w-3.5 h-3.5 shrink-0" />
        <span>{noteExists ? "View notes" : "Add notes"}</span>
      </Link>

      {isLocked && (
        <p className="text-xs text-muted-foreground flex items-center gap-1 pl-0.5" data-testid="text-note-summary-locked">
          <Lock className="w-3 h-3 shrink-0" />
          Being edited by {note.lockedBy?.username ?? "someone"}
        </p>
      )}

      {noteExists && note.updatedBy && note.updatedAt && !isLocked && (
        <p className="text-xs text-muted-foreground pl-0.5" data-testid="text-note-summary-last-edited">
          Last edited by {note.updatedBy.username}, {formatTimeAgo(note.updatedAt)}
        </p>
      )}
    </div>
  );
}
